import { countries } from 'country-list-json';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { createContext, useContext } from 'react';
import { z } from 'zod';

const countryCodes = countries.map((country) => country.code);

export const UserProfileSchema = z.object({
  firstName: z.string().max(256),
  middleName: z.string().max(256),
  lastName: z.string().max(256),
  email: z.string().trim().email().max(256),
  phone: z.string().refine(
    // Empty is allowed here so an optional phone can be left blank; required
    // phones are still enforced by the field's own required handling.
    (value) => value === '' || isValidPhoneNumber(value),
    'Must be a valid phone number (e.g. +1 555 123 4567)',
  ),
  address: z.object({
    streetAddress: z.string().max(256),
    locality: z.string().max(256),
    region: z.enum([
      'AL',
      'AK',
      'AS',
      'AZ',
      'AR',
      'AA',
      'AE',
      'AP',
      'CA',
      'CO',
      'CT',
      'DE',
      'DC',
      'FM',
      'FL',
      'GA',
      'GU',
      'HI',
      'ID',
      'IL',
      'IN',
      'IA',
      'KS',
      'KY',
      'LA',
      'ME',
      'MH',
      'MD',
      'MA',
      'MI',
      'MN',
      'MS',
      'MO',
      'MT',
      'NE',
      'NV',
      'NH',
      'NJ',
      'NM',
      'NY',
      'NC',
      'ND',
      'MP',
      'OH',
      'OK',
      'OR',
      'PW',
      'PA',
      'PR',
      'RI',
      'SC',
      'SD',
      'TN',
      'TX',
      'UT',
      'VT',
      'VI',
      'VA',
      'WA',
      'WV',
      'WI',
      'WY',
    ]),
    postalCode: z
      .string()
      .trim()
      .regex(/^\d{5}$/, 'Must be a 5-digit zip code (e.g. 12345)'),
    country: z.enum(countryCodes as [string, ...string[]]),
  }),
  age: z.number().int().positive(),
  birthdate: z.date().max(new Date(), { message: 'Has not been born yet!' }),
  gender: z.enum(['unspecified', 'female', 'male']),
  binaryGender: z.enum(['female', 'male']),
  ethnicity: z.enum([
    'HispanicOrLatino',
    'AmericanIndianOrAlaskaNative',
    'Asian',
    'BlackOrAfricanAmerican',
    'NativeHawaiianOrOtherPacificIslander',
    'White',
    'PreferNotToSay',
  ]),
  eyeColor: z.string().max(256),
  hairColor: z.string().max(256),
  height: z
    .number({ required_error: 'Required', invalid_type_error: 'Required' })
    .int()
    .min(24, 'Height must be between 2 and 8 feet')
    .max(96, 'Height must be between 2 and 8 feet'),
  weight: z
    .number({ required_error: 'Required', invalid_type_error: 'Required' })
    .int()
    .positive(),
  ssn: z.string().regex(/^\d+$/, 'SSN must contain numbers only'),
  driversLicense: z
    .string()
    .regex(
      /^(\d{7,8})?$/,
      "Driver's License must be empty or contain 7-8 numbers",
    ),
  customData: z
    .object({
      californiaBoaterCardApplicationId: z.string(),
      floridaFWCPIN: z
        .string()
        .regex(/^\d+$/, 'PIN must contain only digits (e.g. 1234)')
        .length(4, 'PIN must contain exactly 4 digits (e.g. 1234)'),
      goOutdoorsID: z.string(),
      arkansasCustomerId: z
        .string()
        .regex(/^\d*$/, 'Must contain numbers only'),
      reasonForCompletingCourse: z.enum([
        'Bass Club Inspector',
        'Boating Event Inspector',
        'Commercial Baitfish or Smelt Wholesaler',
        'Other',
      ]),
    })
    .passthrough(), // Allow any other custom data
  // True while the saved profile is first being fetched. Never true without a
  // signed-in user, since there's no profile to fetch.
  loading: z.boolean(),
  saving: z.boolean(),
  dirty: z.boolean(),
  validationErrors: z.array(
    z.object({ path: z.string(), message: z.string() }),
  ),
});

export type UserProfileValidationError = z.infer<
  typeof UserProfileSchema.shape.validationErrors.element
>;

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export type UserProfile = DeepPartial<z.infer<typeof UserProfileSchema>>;

export interface UserProfileActions {
  get savedProfile(): UserProfile;
  updateProfile(profile: UserProfile): void;
  saveProfile(profile: UserProfile, onSuccess?: () => void): Promise<void>;
  loadProfile(): void;
  isCalculatedField(path: string): boolean;
  isDirtyField(path: string): boolean;
}

export const UserProfileContext = createContext<
  UserProfile & UserProfileActions
>({
  get savedProfile() {
    return {};
  },
  updateProfile() {},
  async saveProfile() {},
  loadProfile() {},
  isCalculatedField() {
    return true;
  },
  isDirtyField() {
    return false;
  },
});

export function useUserProfile() {
  return useContext(UserProfileContext);
}
